import { useEffect, useState } from 'react';
import Modal from 'react-modal';
import Cookies from 'js-cookie';
import axios from 'axios';
import { Analytics } from 'analytics';
import googleTagManager from '@analytics/google-tag-manager';
import YouInvest from '../../../Api/YouInvest';
import { isProd } from 'src/Utils';

export default function CookieConsentModal(props) {

    // mimics https://www.ajbell.co.uk/sites/all/modules/custom/ajbell_cookies/ajbell_cookies.js
    // C0000 - essential
    // C0002 - marketing
    // C0003 - performance

    const [isOpen, setIsOpen] = useState(false);
    const [origin, setOrigin] = useState('');

    const whenJQueryReady = (executeThis) => {
        if (window.jQuery) {
            executeThis();
        } else {
            setTimeout(() => {
                whenJQueryReady(executeThis);
            }, 500);
        }
    }

    const setGUID = (cookieAgreedValue) => {
        const gidAjbValue = Cookies.get('gid_ajb');
        if (gidAjbValue && cookieAgreedValue.includes('C0002')) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ gid_ajb: gidAjbValue });
            return;
        }
        if (!cookieAgreedValue.includes('C0002')) {
            Cookies.remove('gid_ajb');
            return;
        }
        // set guid cookie
        axios.get('/api/user/set-guid', {
            headers: YouInvest.getTradingRequestHeaders()
        }).catch((error) => {
            console.log('Failed to set GUID', error);
        });
    }

    const enableScripts = (cookieAgreedValue) => {

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ cookieAgreed: cookieAgreedValue });

        // load external scripts
        const agreedPolicies = cookieAgreedValue.split(',');
        for (const i in agreedPolicies) {
            const selector = 'script.ajb-cookie-consent-' + agreedPolicies[i];
            whenJQueryReady(() => {
                jQuery(selector).each((index, script) => {
                    if (script.src) {
                        jQuery.getScript(script.src);
                    }
                });
            });
        }

        // add analytics
        if (cookieAgreedValue.includes('C0003')) {
            const tagManagerId = isProd() ? 'GTM-WKV9WP' : 'GTM-N5Q8W4';
            const analytics = Analytics({
                app: 'morningstar', // Call this whatever you like.
                plugins: [
                    googleTagManager({
                        containerId: tagManagerId,
                        enabled: true
                    })
                ]
            });
            analytics.page().catch((error) => {
                console.log(error);
            });
        }
    }

    const setReferrer = () => {
        const queryString = decodeURIComponent(window.location.search.substring(1));
        if (queryString) {
            const params = queryString.split('&');
            for (const i in params) {
                const keyValue = params[i].split('=');
                if ((keyValue[0] === 'sitereferrer' || keyValue[0] === 'SSKEY') && keyValue[1]) {
                    const expires = keyValue[1] == 'starlingBank' ? 1 : 30;
                    Cookies.set('referrer', keyValue[1], { expires: expires });
                }
            }
        }
    }

    useEffect(() => {

        setOrigin(window.location.origin);

        // delete stray eu cookie module value set during rebrand testing
        Cookies.remove('cookie-agreed', { path: '/group' });

        const cookieAgreedValue = Cookies.get('cookie-agreed');

        if (!cookieAgreedValue) {
            // remove any old cookies
            Cookies.remove('referrer');
            Cookies.remove('gid_ajb');
            // show modal if not suppressed
            if (window.location.hash !== '#banner') {
                setIsOpen(true);
            }
        } else {
            setGUID(cookieAgreedValue);
            enableScripts(cookieAgreedValue);
        }

        if (cookieAgreedValue?.includes('C0002')) {
            setReferrer();
        }
    }, []);

    const acceptAll = (e) => {
        e.preventDefault();
        const allPolicies = 'C0000,C0002,C0003';
        Cookies.set('cookie-agreed', allPolicies, { expires: 100 });
        setReferrer();
        enableScripts(allPolicies);
        setIsOpen(false);
    }

    const customStyles = {
        overlay: {
            zIndex: 9999,
            backgroundColor: 'rgba(0, 0, 0, 0.5)'
        },
        content: {
            padding: '0',
            margin: '0',
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            display: 'block'
        },
    };

    return <Modal
        isOpen={isOpen}
        style={customStyles}
        shouldCloseOnOverlayClick={false}
        shouldFocusAfterRender={false}
        preventScroll={true}
        className="modal"
        testId="cookieConsentModal"
        contentLabel="Cookie preferences popup"
        ariaHideApp={false}>
        <div className="modal-content" data-role="dialog" aria-modal="true" aria-label="modal" style={{ padding: "20px 20px 0" }}>
            <h4 className="h4">This website uses cookies</h4>
            <div className="text-align-center">
                <p className="text-align-left">
                    Our website uses cookies to give you a better browsing experience.<br />
                    You can choose to accept all cookies, or control which we use by clicking &apos;Manage cookies&apos;.
                    To learn more, read <a data-testid="cookieConsentPolicyLink" href={origin + '/cookies'}>our cookie policy</a>.
                </p>
            </div>
        </div>
        <div className="modal-footer padding-20 white">
            <a data-testid="cookieConsentManage" href={origin + '/cookies/preferences#banner'} className="c-btn button-outline waves-effect grey-text text-darken-4">Manage cookies</a>
            <a data-testid="cookieConsentAcceptAll" href="#accept-all" className="c-btn button--grey waves-effect margin-left-20" onClick={acceptAll}>Accept all</a>
        </div>
    </Modal>
}
